"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhatIsForestryMulching() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const intro = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const symbol = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const features = [{
    label: "ECO-FRIENDLY CLEARING",
    title: "Nutrient-rich mulch for your soil",
    description: "Our specialized machinery grinds vegetation into a fine mulch on the spot, returning vital organic matter to the earth while preventing erosion.",
    image: {
      src: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/05/Newsom-after-2.jpg?v=v1b82wcmoz9",
      srcset: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/05/Newsom-after-2.jpg?v=qxbn60ol43d-wvc-srcset",
      sizes: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/05/Newsom-after-2.jpg?v=uqb5xj3xqzc-wvc-sizes",
      alt: "Forestry mulcher processing brush into mulch"
    },
    offset: "md:mt-0"
  }, {
    label: "PRECISION LAND CARE",
    title: "Selective removal, lasting health",
    description: "Unlike traditional clearing, mulching allows us to remove invasive species and undergrowth while leaving desirable trees completely untouched.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/machine-cropped.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/machine-cropped.png",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/machine-cropped.png",
      alt: "Cleared land with healthy standing trees"
    },
    offset: "md:mt-32"
  }, {
    label: "SINGLE-STEP PROCESS",
    title: "No hauling, no burning, no waste",
    description: "We eliminate the need for debris piles or costly transport. One machine handles the cutting, grinding, and spreading in a single efficient pass.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/newsombefore-after-quadrant.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/cleared_land_landscape.png",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/cleared_land_landscape.png",
      alt: "Finished mulched ground surface"
    },
    offset: "md:mt-16"
  }];
  return <section data-section-id="3799" id="what-is-forestry-mulching" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 px-6 md:px-12 lg:px-20">
      {}
      <div ref={symbol.ref} aria-hidden="true" className={`pointer-events-none absolute top-8 right-6 md:right-12 lg:right-20 text-[8rem] md:text-[12rem] leading-none font-default font-bold select-none transition-all duration-1000 ease-out ${symbol.isVisible ? "opacity-10 translate-y-0" : "opacity-0 -translate-y-4"}`} style={{
      color: "var(--primary)"
    }}>
        †
      </div>

      <div className="relative mx-auto max-w-7xl">
        {}
        <div ref={eyebrow.ref} className={`transition-all duration-700 ease-out ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-mono text-xs md:text-sm tracking-[0.25em] uppercase font-semibold" style={{
          color: "var(--primary)"
        }}>
            01 &nbsp;◦&nbsp; THE MULCHING ADVANTAGE
          </span>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mt-10 md:mt-14">
          <h2 ref={heading.ref} className={`md:col-span-8 md:col-start-1 font-default font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tighter transition-all duration-1000 ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            Land clearing
            <br />
            <span className="pl-8 md:pl-16 text-primary">reimagined for nature</span>
          </h2>

          <div className="md:col-span-4 md:col-start-9 flex md:items-end">
            <p ref={intro.ref} className={`font-default text-base md:text-lg leading-relaxed transition-all duration-1000 delay-200 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            color: "var(--muted-foreground)"
          }}>
              Forestry mulching is the most efficient, eco-friendly way to manage overgrowth. At Chipper Mulching Co, we transform dense brush into a protective layer of mulch that enriches your soil.
            </p>
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {features.map((feature, i) => {
          const card = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div data-index={i} key={i} ref={card.ref} className={`${feature.offset} ${i % 2 === 1 ? "ml-4 md:ml-0" : "mr-4 md:mr-0"} transition-all duration-1000 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <div className="group rounded-lg p-8 md:p-10 transition-all duration-500 ease-out hover:-translate-y-1.5 shadow-md hover:shadow-xl border border-border" style={{
              backgroundColor: "var(--card)"
            }}>
                  <div className="overflow-hidden rounded-md aspect-square">
                    <img src={feature.image.src} data-wvc-srcset={feature.image.srcset} data-wvc-sizes={feature.image.sizes} alt={feature.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]" />
                  </div>

                  <div className="mt-8">
                    <span className="font-mono text-[0.7rem] md:text-xs tracking-[0.22em] uppercase font-bold" style={{
                  color: "var(--secondary)"
                }}>
                      {feature.label}
                    </span>

                    <h3 className="font-default font-bold text-2xl md:text-[1.7rem] leading-snug mt-4" style={{
                  color: "var(--foreground)"
                }}>
                      {feature.title}
                    </h3>

                    <p className="font-default text-base leading-relaxed mt-4" style={{
                  color: "var(--muted-foreground)"
                }}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}